// HFS a11y (JS-21): only return controls a keyboard user can actually reach. Hidden, visibility:hidden and [inert]
// elements are dropped, and [draggable] only matches draggable="true" (draggable="false" is not focusable).
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable=true], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter((el) => {
    if (el.tabIndex < 0 || el.closest('[inert], [hidden]')) return false;
    // Closed native details can retain nonzero layout rects (content-visibility),
    // but only their first direct summary remains keyboard-reachable.
    for (let ancestor = el.parentElement; ancestor; ancestor = ancestor.parentElement) {
      if (ancestor.tagName !== 'DETAILS' || ancestor.open) continue;
      const summary = Array.from(ancestor.children).find((child) => child.tagName === 'SUMMARY');
      if (!summary || !summary.contains(el)) return false;
    }
    if (!el.getClientRects().length) return false;
    return getComputedStyle(el).visibility === 'visible';
  });
}

// HFS (general review 2026-10-01, image efficiency): point an <img> at another Shopify CDN image without losing its
// responsive candidates. The srcset keeps the image's own width descriptors (each candidate is requested at that
// width from the new image) and src keeps its width parameter, so a colour swap never loads one full-size original
// into a small card, gallery thumbnail or zoom slot. Used by the card swatches, the colour-filter card updates and
// the product page colour picker; each caller falls back to a plain src swap if this file has not loaded.
function hfsSizedImageUrl(url, width) {
  if (!url || !width) return url;
  const hashAt = url.indexOf('#');
  const hash = hashAt > -1 ? url.slice(hashAt) : '';
  const bare = hashAt > -1 ? url.slice(0, hashAt) : url;
  const queryAt = bare.indexOf('?');
  const base = queryAt > -1 ? bare.slice(0, queryAt) : bare;
  const params = queryAt > -1 ? bare.slice(queryAt + 1).split('&').filter((param) => param && !/^width=/i.test(param)) : [];
  params.push(`width=${width}`);
  return `${base}?${params.join('&')}${hash}`;
}

function hfsSwapImage(img, url) {
  if (!img || !url) return;
  const widths = (img.getAttribute('srcset') || '')
    .split(',')
    .map((candidate) => {
      const match = /\s(\d+)w$/.exec(candidate.trim());
      return match ? Number(match[1]) : 0;
    })
    .filter((width, index, all) => width > 0 && all.indexOf(width) === index);
  if (widths.length) {
    img.setAttribute('srcset', widths.map((width) => `${hfsSizedImageUrl(url, width)} ${width}w`).join(', '));
  } else {
    img.removeAttribute('srcset'); // an "x" or empty srcset would keep showing the previous image
  }
  const srcWidth = /[?&]width=(\d+)/.exec(img.getAttribute('src') || '');
  img.setAttribute('src', srcWidth ? hfsSizedImageUrl(url, Number(srcWidth[1])) : url);
}
window.hfsSwapImage = hfsSwapImage;

/* HFS (Semrush/Clarity SC11, 2026-10-01): a colour chosen on a product card goes with the shopper. The card's product
   links (the title link, which also covers the photo, and Quick add) name a variant of that colour (data-variant-id
   on the swatch) and say that only its colour was chosen (hfs_pick=color, see hfsRequestedOptions); the product page
   starts on that colour and leaves size and width to the shopper. Called for a shopper's choice and for swatches
   that colour-filter scripts check. */
function hfsCardColourLinks(input) {
  const card = input && input.closest('.card-wrapper');
  const variantId = input && input.dataset.variantId;
  if (!card || !variantId) return;
  card.querySelectorAll('a.full-unstyled-link[href], [data-product-url]').forEach((link) => {
    const attribute = link.hasAttribute('data-product-url') ? 'data-product-url' : 'href';
    const value = link.getAttribute(attribute);
    if (!value || value.indexOf('/products/') === -1) return;
    const url = new URL(value, window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('hfs_pick', 'color');
    link.setAttribute(attribute, url.pathname + url.search + url.hash);
  });
}
window.hfsCardColourLinks = hfsCardColourLinks;

/* HFS (M1 complete variant intent, 2026-10-01): which options a product link asks the variant picker to choose. A link
   that names a variant (a shopping feed or ad, a shared link, the cart) asks for all of that variant's options. The
   theme's card links add hfs_pick=color: their variant only stands for the colour chosen on the card, so size and
   width stay with the shopper, as they do in Quick add, which opens from a card. The variant's options come from the
   picker's data-hfs-requested (snippets/hfs-requested-variant.liquid), present only when Shopify recognised the
   variant. Only the page's own product reads the link (another product's picker on the page, such as a featured
   product, does not). Returns the options to choose besides the colour (product-variant-options.liquid has chosen
   it), keyed by lower-case option name, and stale: true when the link names a variant this product does not have.
   Paths are compared without a trailing slash, letter case or percent-encoding (/products/Shoe/ is /products/shoe). */
function hfsRequestedOptions(picker) {
  const result = { options: {}, stale: false };
  if (!picker || picker.closest('quick-add-modal')) return result;
  const samePath = (path) => {
    let value = path || '';
    try {
      value = decodeURIComponent(value);
    } catch (error) {
      // a malformed escape is compared as it is
    }
    return value.toLowerCase().replace(/\/+$/, '');
  };
  const productPath = samePath((picker.dataset.url || '').split(/[?#]/)[0]);
  if (!productPath || !samePath(window.location.pathname).endsWith(productPath)) return result;
  const params = new URLSearchParams(window.location.search);
  if (!params.get('variant')) return result;
  let requested = null;
  try {
    requested = JSON.parse(picker.dataset.hfsRequested || 'null');
  } catch (error) {
    requested = null;
  }
  if (!requested || typeof requested !== 'object') {
    result.stale = true;
    return result;
  }
  const same = (name) => (name === 'colour' ? 'color' : name);
  const pick = (params.get('hfs_pick') || '').toLowerCase().split(',').map((name) => same(name.trim())).filter(Boolean);
  Object.keys(requested).forEach((name) => {
    const key = name.toLowerCase();
    if (same(key) === 'color' || (pick.length && !pick.includes(same(key)))) return;
    if (requested[name] != null && requested[name] !== '') result.options[key] = String(requested[name]);
  });
  return result;
}
window.hfsRequestedOptions = hfsRequestedOptions;

// HFS (M1): a plain line at the top of the picker when the link's variant, or one of its options, is not offered. It is
// a status message, and it goes when the shopper makes a choice of their own (hfsClearLinkNote).
function hfsLinkNote(picker) {
  if (!picker || picker.querySelector('.hfs-link-note')) return;
  const note = document.createElement('p');
  note.className = 'hfs-link-note';
  note.setAttribute('role', 'status');
  note.textContent = 'The option in your link is no longer available. Please choose from the options below.';
  picker.prepend(note);
}
window.hfsLinkNote = hfsLinkNote;
function hfsClearLinkNote(picker) {
  const note = picker && picker.querySelector('.hfs-link-note');
  if (note) note.remove();
}
window.hfsClearLinkNote = hfsClearLinkNote;

function initDetailsSummary(summary) {
  const details = summary.parentElement;
  if (!details || details.tagName !== 'DETAILS') return;
  summary.setAttribute('role', 'button');
  const sync = () => summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  sync();

  if (summary.nextElementSibling?.id) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }
  if (summary.hfsDetailsBound) return;
  summary.hfsDetailsBound = true;
  if (!details.hfsToggleBound) {
    details.hfsToggleBound = true;
    details.addEventListener('toggle', () => {
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    });
  }
  if (summary.closest('header-drawer, menu-drawer')) return;
  details.addEventListener('keyup', onKeyUpEscape);
}
document.querySelectorAll('[id^="Details-"] summary').forEach(initDetailsSummary);

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();
  if (!container) return;
  if (container.tabIndex < 0 && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');

  trapFocusHandlers.focusin = (event) => {
    if (!container.contains(event.target)) (getFocusableElements(container)[0] || container).focus();
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.key !== 'Tab') return; // If not TAB key (event.code can be undefined on autofill/IME events)
    const current = getFocusableElements(container);
    const first = current[0] || container;
    const last = current[current.length - 1] || container;
    if (!current.length || !container.contains(event.target) ||
        (event.target === last && !event.shiftKey)) {
      event.preventDefault();
      first.focus();
    } else if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusin', trapFocusHandlers.focusin);
  document.addEventListener('keydown', trapFocusHandlers.keydown);
  elementToFocus = elementToFocus && container.contains(elementToFocus) ? elementToFocus : container;
  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (event.code && navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.key !== 'Escape') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    // HFS a11y (CART-02, demand item b): Enter in a quantity field commits the number instead of implicitly
    // submitting the surrounding form (cart: "Check out"; product page: Add to cart and open the drawer).
    this.committedValue = this.input.value;
    this.input.addEventListener('change', () => {
      this.committedValue = this.input.value;
    });
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.isComposing) return;
      event.preventDefault();
      if (this.input.value !== this.committedValue) this.input.dispatchEvent(this.changeEvent);
    });
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;
    event.currentTarget.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
      buttonMinus.setAttribute('aria-disabled', String(value <= min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
      buttonPlus.setAttribute('aria-disabled', String(value >= max));
    } else {
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) {
        buttonPlus.classList.remove('disabled');
        buttonPlus.setAttribute('aria-disabled', 'false');
      }
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  connectedCallback() {
    this.onDrawerResize = this.onDrawerResize || (() => {
      if (!this.mainDetailsToggle.open || this.getClientRects().length) return;
      const hadFocus = this.contains(document.activeElement);
      this.closeMenuDrawer(new Event('resize'));
      if (hadFocus) {
        const region = this.closest('.section-header') || document.querySelector('main') || document.body;
        const target = getFocusableElements(region)[0] || region;
        if (target === region && region.tabIndex < 0) region.setAttribute('tabindex', '-1');
        target.focus();
      }
    });
    window.addEventListener('resize', this.onDrawerResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onDrawerResize);
    if (this.mainDetailsToggle.open) this.closeMenuDrawer(new Event('disconnect'));
  }

  bindEvents() {
    this.boundControls = this.boundControls || new WeakSet();
    this.querySelectorAll('summary').forEach((summary) => {
      initDetailsSummary(summary);
      if (this.boundControls.has(summary)) return;
      this.boundControls.add(summary);
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });
    this.querySelectorAll('.menu-drawer__close-button, .mobile-facets__close-button, [data-menu-drawer-close]').forEach((button) => {
      if (this.boundControls.has(button)) return;
      this.boundControls.add(button);
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    });
  }

  onKeyUp(event) {
    if (event.key !== 'Escape') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;
    event.stopPropagation();

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    event.preventDefault();
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');

    if (detailsElement === this.mainDetailsToggle) {
      if (!isOpen) detailsElement.setAttribute('open', '');
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)').matches) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      if (isOpen) return this.closeSubmenu(detailsElement);
      detailsElement.setAttribute('open', '');
      const focusRequest = (this.submenuFocusRequest || 0) + 1;
      this.submenuFocusRequest = focusRequest;
      setTimeout(() => {
        const isCurrent = () => this.isConnected && detailsElement.open && this.mainDetailsToggle.open && this.submenuFocusRequest === focusRequest;
        if (!isCurrent()) return;
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        // HFS (browser audit 2026-10-01, B01): focus moves into the submenu BEFORE .submenu-open hides the parent
        // list. A native visibility transition can keep the panel hidden for more than one frame. Wait until the
        // target is visible, retry within a bounded frame/time budget, and abandon a closed/replaced/disconnected
        // request. Reduced motion succeeds on the first visible frame without an artificial delay.
        const submenu = summaryElement.nextElementSibling;
        let focusAttempts = 0;
        const focusDeadline = performance.now() + 750;
        const focusSubmenu = () => {
          if (!isCurrent()) return;
          const target = submenu.contains(document.activeElement) ? document.activeElement : detailsElement.querySelector('button');
          if (target?.getClientRects().length && getComputedStyle(target).visibility === 'visible') {
            trapFocus(submenu, target);
            if (submenu.contains(document.activeElement)) {
              parentMenuElement && parentMenuElement.classList.add('submenu-open');
              return;
            }
          }
          focusAttempts++;
          if (focusAttempts < 45 && performance.now() < focusDeadline) requestAnimationFrame(focusSubmenu);
        };
        focusSubmenu();
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      if (this.mainDetailsToggle.open) this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    const dialog = this.mainDetailsToggle.querySelector('[role="dialog"]') || this.mainDetailsToggle;
    trapFocus(dialog, getFocusableElements(dialog)[0] || dialog);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.mainDetailsToggle.removeAttribute('open');

    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', 'false');
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.mainDetailsToggle.hasAttribute('open')) return;
      const active = document.activeElement;
      if (this.mainDetailsToggle.contains(active)) return;
      // HFS (browser audit 2026-10-01, B01): focus that fell to the document (the focused control was hidden by a
      // submenu transition or replaced) is not the user leaving the drawer. Keep the drawer open and put focus back
      // on its current panel. Focus moved to a real element outside still closes the drawer.
      if (!active || active === document.body || active === document.documentElement) {
        this.refocusCurrentPanel();
        return;
      }
      this.closeMenuDrawer();
    });
  }

  // The innermost open panel: the deepest open submenu, else the drawer dialog itself.
  refocusCurrentPanel() {
    const open = this.mainDetailsToggle.querySelectorAll('details[open]');
    const deepest = open[open.length - 1];
    const panel = deepest
      ? deepest.querySelector(':scope > summary')?.nextElementSibling || deepest
      : this.mainDetailsToggle.querySelector('[role="dialog"]') || this.mainDetailsToggle;
    const target = getFocusableElements(panel)[0] || panel;
    if (target === panel && panel.tabIndex < 0 && !panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
    target.focus();
  }

  onCloseButtonClick(event) {
    event.preventDefault();
    const detailsElement = event.currentTarget.closest('details');
    if (event.currentTarget.hasAttribute('data-menu-drawer-close') || detailsElement === this.mainDetailsToggle) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
    } else {
      this.closeSubmenu(detailsElement);
    }
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    detailsElement.removeAttribute('open');
    const parentDetails = detailsElement.parentElement.closest('details[open]');
    const trapContainer = parentDetails === this.mainDetailsToggle && this.tagName !== 'HEADER-DRAWER'
      ? parentDetails.querySelector('[role="dialog"]') || parentDetails
      : parentDetails;
    trapFocus(trapContainer, detailsElement.querySelector('summary'));
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      if (this.mainDetailsToggle.open) this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    this.setPageInert(true);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header?.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
    this.setPageInert(false);
  }

  // While the drawer is open the page behind it must not be reachable by Tab or a screen reader's virtual cursor.
  setPageInert(state) {
    if (state) {
      this.inertElements = Array.from(document.querySelectorAll('main, .shopify-section-group-footer-group'))
        .map((element) => ({ element, wasInert: element.inert }));
      this.inertElements.forEach(({ element }) => { element.inert = true; });
    } else {
      (this.inertElements || []).forEach(({ element, wasInert }) => { element.inert = wasInert; });
      this.inertElements = [];
    }
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

// HFS (JS-30): SlideshowComponent (<slideshow-component>) was removed with the unused sections/slideshow.liquid and
// sections/announcement-bar.liquid; no template or section group used them. Recover from git if one is re-added.

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    this.updateOptions();
    if (!this.options.length || !this.options.every((value) => value != null && value !== '')) return;
    // HFS (FBL-003): the initial sync waits for DOMContentLoaded, by when every deferred script (media-gallery.js,
    // pickup-availability.js) has run. Deferred scripts run while readyState is already 'interactive', so a
    // readyState === 'loading' test alone is not enough; window.hfsDomContentLoaded records that the event has fired.
    if (document.readyState === 'complete' || window.hfsDomContentLoaded) {
      this.syncFromPage();
      return;
    }
    let done = false;
    const initialSync = () => {
      if (done || !this.isConnected) return;
      done = true;
      this.updateOptions();
      if (this.options.length && this.options.every((value) => value != null && value !== '')) this.syncFromPage();
    };
    document.addEventListener('DOMContentLoaded', initialSync, { once: true });
    window.addEventListener('load', initialSync, { once: true });
  }

  // HFS (M1): the first sync shows the variant the page opened with, so it leaves the address as it is (campaign
  // parameters included); choices made afterwards update it as before (updateURL).
  syncFromPage() {
    this.hfsSyncingFromPage = true;
    try {
      this.onVariantChange();
    } finally {
      this.hfsSyncingFromPage = false;
    }
  }

  onVariantChange(event) {
    if (event) hfsClearLinkNote(this); // HFS (M1): the shopper's own choice replaces the link's note
    this.requestToken = (this.requestToken || 0) + 1;
    this.updateOptions();
    this.updateMasterId();
    // HFS (FBL-005): a variant that the embedded JSON lists as available keeps Add usable while the section fetch
    // runs (the id is written below and the server validates quantity rules); anything else stays unavailable.
    if (this.currentVariant && this.currentVariant.available) this.toggleAddButton(false);
    else this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    // Dynamic label
    ['Color', 'Colour', 'Size', 'Width'].forEach((field) => {
        var radioInput = Array.from(this.querySelectorAll('input:checked')).find((input) => (input.dataset.optionName || input.name) === field);
        var labelElement = this.querySelector('[id="selected-variant-' + field.toLowerCase() + '-label"]');
    
        if (radioInput && labelElement) {
            var selectVariantValue = radioInput.dataset.label || radioInput.value; // HFS: display name, no "(1)"
            labelElement.textContent = ' ' + selectVariantValue;
        }
    });

    if (!this.currentVariant) {
      this.updateVariantInput();
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
      this.updateColourMedia(event); // HFS (FBL-004)
    } else {
      this.updateMedia();
      this.updateURL();
      // HFS (preview fixes 2026-09-30): REG-01, price and quantity rules follow the selected variant from data already
      // in the page before [name=id] changes; the section fetch below still replaces them, and a failed fetch keeps them.
      this.updatePriceFromData();
      this.updateQuantityRulesFromData();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }


  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  // HFS (FBL-004): after a colour choice that does not yet resolve a full variant, show the first variant of that
  // colour through the media gallery (the slides keep their own alt). The hidden id is not written.
  updateColourMedia(event) {
    const target = event && event.target;
    if (!target || !target.closest) return;
    const groups = Array.from(this.querySelectorAll(this instanceof VariantRadios ? 'fieldset' : 'select'));
    const colourIndex = groups.findIndex((group) => {
      const control = group.matches('select') ? group : group.querySelector('input');
      if (!control) return false;
      const name = control.dataset.optionName || (control.name || '').replace(/^options\[(.*)\]$/, '$1').replace(/-[^-]*$/, '');
      return /^colou?r$/i.test(name.trim());
    });
    if (colourIndex < 0 || !groups[colourIndex].contains(target)) return;
    const colour = this.options[colourIndex];
    if (colour == null || colour === '') return;
    const match = this.getVariantData().find((variant) => variant.options[colourIndex] === colour && variant.featured_media);
    if (match) this.updateMedia(match);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia(variant = this.currentVariant) {
    if (!variant) return;
    if (!variant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    const mediaId = `${this.dataset.section}-${variant.featured_media.id}`;
    const requestToken = this.requestToken;
    // HFS (FBL-003): media-gallery.js is deferred after global.js; wait for the element instead of throwing.
    mediaGalleries.forEach((mediaGallery) => {
      if (typeof mediaGallery.setActiveMedia === 'function') {
        mediaGallery.setActiveMedia(mediaId, true);
        return;
      }
      customElements.whenDefined('media-gallery').then(() => {
        if (requestToken === this.requestToken && typeof mediaGallery.setActiveMedia === 'function') mediaGallery.setActiveMedia(mediaId, true);
      });
    });

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
    if (newMediaModal) modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false' || this.hfsSyncingFromPage) return;
    if (this.closest('quick-add-modal')) return; // HFS (FBL-018): a quick-add modal never rewrites the host page URL
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input) return;
      input.value = this.currentVariant ? this.currentVariant.id : '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const availableOptionInputsValue = this.getVariantData()
        .filter((variant) => variant.available && variant.options.every((value, position) => position === index || !this.options[position] || this.options[position] === value))
        .map((variant) => variant.options[index]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      const label = input.dataset.label || input.getAttribute('value');
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = label;
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', label);
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = this.closest('section')?.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      // HFS (FBL-003): pickup-availability.js is deferred after global.js; wait for the element instead of throwing.
      const variantId = this.currentVariant.id;
      const requestToken = this.requestToken;
      if (typeof pickUpAvailability.fetchAvailability === 'function') pickUpAvailability.fetchAvailability(variantId);
      else customElements.whenDefined('pickup-availability').then(() => {
        if (requestToken === this.requestToken && typeof pickUpAvailability.fetchAvailability === 'function') pickUpAvailability.fetchAvailability(variantId);
      });
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm && productForm.handleErrorMessage) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const requestToken = this.requestToken;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(
      `${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
      .then((response) => {
        if (!response.ok) throw new Error('Product details unavailable');
        return response.text();
      })
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (!this.currentVariant || this.currentVariant.id !== requestedVariantId || requestToken !== this.requestToken) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // HFS (FBL-005): a response without this section's Add button is a failed refresh (handled in catch).
        if (!html.getElementById(`ProductSubmitButton-${sectionId}`)) throw new Error('Product details incomplete');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        const volumePricingSource = html.getElementById(
          `Volume-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );

        const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
        const pricePerItemSource = html.getElementById(`Price-Per-Item-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);

        const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);

        // HFS (preview fixes 2026-09-30): the block is role="status"; when the instant update (updatePriceFromData) already
        // produced the same text, leave it so the price is not announced a second time.
        if (source && destination && destination.textContent.replace(/\s+/g, ' ').trim() !== source.textContent.replace(/\s+/g, ' ').trim()) {
          destination.innerHTML = source.innerHTML;
        }
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
        }

        if (volumePricingSource && volumePricingDestination) {
          volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
        }

        if (pricePerItemSource && pricePerItemDestination) {
          pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
          pricePerItemDestination.classList.toggle('visibility-hidden', pricePerItemSource.classList.contains('visibility-hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');

        if (inventoryDestination && inventorySource)
          inventoryDestination.classList.toggle('visibility-hidden', inventorySource.textContent.trim() === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        const nowUnavailable = addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true;
        this.toggleAddButton(nowUnavailable, window.variantStrings.soldOut);
        // HFS a11y (PDP-02/PDP-12): say so when the chosen combination cannot be bought (price updates announce themselves)
        if (nowUnavailable && window.hfsPdp) {
          window.hfsPdp.announce(`${this.currentVariant.title}: ${window.variantStrings.soldOut}`);
        }

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
        });
      })
      .catch(() => {
        if (requestToken !== this.requestToken) return;
        const productForm = document.getElementById(`product-form-${this.dataset.section}`)?.closest('product-form');
        // HFS (FBL-005): when [name=id] already holds a variant the embedded JSON lists as available, Add stays
        // enabled (the server validates the add) and the shopper can retry the price/availability refresh.
        const idInput = document.querySelector(`#product-form-${this.dataset.section} [name="id"]`);
        const heldVariant = idInput && idInput.value && this.getVariantData().find((variant) => String(variant.id) === idInput.value);
        if (heldVariant && heldVariant.available) {
          this.toggleAddButton(false);
          this.showRefreshError(productForm);
          return;
        }
        this.toggleAddButton(true, window.variantStrings.unavailable);
        if (productForm && productForm.handleErrorMessage) productForm.handleErrorMessage('Could not update this option. Choose another option, then try again.');
      });
  }

  // HFS (FBL-005): "could not be refreshed" message with a Retry button in this form's error wrapper.
  showRefreshError(productForm) {
    if (!productForm || !productForm.handleErrorMessage) return;
    productForm.handleErrorMessage('Price and availability could not be refreshed.', true);
    const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
    if (!wrapper || wrapper.hidden) return;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button button--secondary hfs-refresh-retry';
    retry.textContent = 'Retry';
    retry.addEventListener('click', () => {
      if (!this.currentVariant) return;
      productForm.handleErrorMessage();
      const addButton = productForm.querySelector('[name="add"]');
      if (addButton) addButton.focus(); // the Retry button is removed with the message
      this.renderProductInfo();
    });
    wrapper.appendChild(retry);
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;
    const payment = productForm.querySelector('.shopify-payment-button');
    if (payment) payment.style.display = disable ? 'none' : '';

    // HFS a11y (PDP-02/JS-19): real unavailable state on this section's Add button. aria-disabled (not `disabled`)
    // keeps it focusable so the "Sold out"/"Unavailable" text can be found; product-form.js refuses to submit
    // while aria-disabled or data-hfs-unavailable is set. The class is scoped to this form's own button.
    if (disable) {
      addButton.classList.add('sold_out');
      addButton.removeAttribute('disabled');
      addButton.setAttribute('aria-disabled', 'true');
      addButton.setAttribute('data-hfs-unavailable', '');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.classList.remove('sold_out');
      addButton.removeAttribute('disabled');
      addButton.removeAttribute('data-hfs-unavailable');
      // product-form.js owns aria-disabled while a request is in flight
      const productFormElement = addButton.closest('product-form');
      if (!productFormElement || !productFormElement.isSubmitting) addButton.removeAttribute('aria-disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (window.hfsPdp) window.hfsPdp.announce(window.variantStrings.unavailable); // HFS a11y (PDP-02)
    if (price) price.classList.add('visibility-hidden');
    if (inventory) inventory.classList.add('visibility-hidden');
    if (sku) sku.classList.add('visibility-hidden');
    if (pricePerItem) pricePerItem.classList.add('visibility-hidden');
  }

  // HFS (preview fixes 2026-09-30): REG-01, money as the store's `money` filter prints it ("$137.49"; with the
  // currency-code setting, "$137.49 USD"). No theme money format exists in the page, so Intl is used.
  formatMoneyFromData(cents, withCode) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    let text;
    try {
      text = new Intl.NumberFormat((window.Shopify && window.Shopify.locale) || 'en-US', { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      text = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
    }
    return withCode ? `${text} ${currency}` : text;
  }

  // HFS (preview fixes 2026-09-30): REG-01, rewrite the price block that snippets/price.liquid rendered for the variant
  // the picker now holds: amounts, price--on-sale / price--sold-out, the discount indicator (.hfs-discount-pct) and the
  // badge percentage (.discounted-price). Existing markup is edited in place (labels such as .cust_sale are kept) so
  // the snippet's wording is not duplicated here. Opt-in: #price-* carries data-hfs-variant-id
  // (sections/main-product.liquid). The unit-price block is left alone.
  updatePriceFromData(variant = this.currentVariant) {
    const container = document.getElementById(`price-${this.dataset.section}`);
    if (!variant || !container || !container.hasAttribute('data-hfs-variant-id')) return;
    container.classList.remove('visibility-hidden');
    const price = container.querySelector('.price');
    if (!price || container.dataset.hfsVariantId === String(variant.id)) return;
    container.dataset.hfsVariantId = String(variant.id);
    const withCode = container.hasAttribute('data-hfs-money-with-currency');
    const amount = this.formatMoneyFromData(variant.price, withCode);
    const compareAt = Number(variant.compare_at_price) || 0;
    const onSale = compareAt > variant.price;
    // Same integer percentage as price.liquid (minus | times: 100 | divided_by).
    const percent = onSale ? `${Math.floor(((compareAt - variant.price) * 100) / compareAt)}%` : '';
    const setAmount = (element, text) => {
      if (!element) return;
      if (!element.children.length) {
        element.textContent = text;
        return;
      }
      Array.from(element.childNodes).forEach((node) => { if (node.nodeType === Node.TEXT_NODE) node.remove(); });
      element.append(` ${text}`);
    };
    price.classList.toggle('price--sold-out', !variant.available);
    if (!container.hasAttribute('data-hfs-volume-pricing')) {
      price.classList.toggle('price--on-sale', onSale);
      setAmount(price.querySelector('.price__regular .price-item--regular'), amount);
    }
    setAmount(price.querySelector('.price__sale .price-item--sale'), amount);
    const was = price.querySelector('.price__sale s.price-item--regular');
    if (was) was.textContent = compareAt ? this.formatMoneyFromData(compareAt, withCode) : '';

    let indicator = price.querySelector('.price__sale .hfs-discount-pct');
    if (indicator) this.discountIndicator = indicator.cloneNode(true);
    const wrapper = price.querySelector('.price__sale .price_wrapper');
    if (!onSale) {
      if (indicator) indicator.remove();
    } else if (indicator || wrapper) {
      if (!indicator) {
        // A copy of the last indicator the server rendered, else the markup snippets/price.liquid prints today.
        indicator = this.discountIndicator ? this.discountIndicator.cloneNode(true) : document.createElement('span');
        if (!this.discountIndicator) {
          indicator.className = 'price-item price-item--discount hfs-discount-pct';
          indicator.setAttribute('style', 'color:#d5202a;font-weight:700;margin-left:6px;white-space:nowrap;');
          indicator.innerHTML = '-0%<span class="visually-hidden"> off</span>';
        }
        wrapper.appendChild(indicator);
      }
      const walker = document.createTreeWalker(indicator, NodeFilter.SHOW_TEXT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) node.data = node.data.replace(/\d+%/, percent);
    }

    const badge = price.querySelector('.price__badge-sale');
    if (!badge) return;
    let badgePercent = badge.querySelector('.discounted-price');
    if (!onSale) {
      if (badgePercent) badgePercent.remove();
      return;
    }
    if (!badgePercent) {
      badgePercent = document.createElement('span');
      badgePercent.className = 'discounted-price';
      const closing = Array.from(badge.childNodes).reverse().find((node) => node.nodeType === Node.TEXT_NODE && node.data.includes(')'));
      badge.insertBefore(badgePercent, closing || null);
    }
    badgePercent.textContent = percent;
  }

  // HFS (preview fixes 2026-09-30): REG-01, quantity rules for the selected variant from the
  // <script id="HfsQtyRules-{section}"> map (min/max/increment/cart per variant id), then product-info's own
  // setQuantityBoundries() derives min/max/step and resets an out-of-range value, as after a section fetch.
  updateQuantityRulesFromData(variant = this.currentVariant) {
    const input = document.getElementById(`Quantity-${this.dataset.section}`);
    const script = document.getElementById(`HfsQtyRules-${this.dataset.section}`);
    if (!variant || !input || !script) return;
    try {
      this.quantityRules = this.quantityRules || JSON.parse(script.textContent);
    } catch (error) {
      return;
    }
    const rule = this.quantityRules[variant.id];
    if (!rule || input.dataset.hfsVariantId === String(variant.id)) return;
    // The input's data-cart-quantity is the latest count for the variant it describes (product-info refreshes it
    // after an add); keep it for when that variant is chosen again.
    const previous = this.quantityRules[input.dataset.hfsVariantId];
    if (previous && input.dataset.cartQuantity) previous.cart = parseInt(input.dataset.cartQuantity, 10) || 0;
    input.dataset.hfsVariantId = String(variant.id);
    const cart = Number(rule.cart) || 0;
    input.dataset.cartQuantity = cart;
    input.dataset.min = rule.min;
    input.min = rule.min;
    if (rule.max == null) {
      delete input.dataset.max;
      input.removeAttribute('max');
    } else {
      input.dataset.max = rule.max;
      input.max = rule.max;
    }
    input.step = rule.increment;
    const quantityForm = document.getElementById(`Quantity-Form-${this.dataset.section}`);
    const rulesText = quantityForm && quantityForm.querySelector('.quantity__rules');
    if (rulesText) {
      const parts = [];
      if (rule.increment > 1) parts.push([script.dataset.multiplesOf, rule.increment]);
      if (rule.min > 1) parts.push([script.dataset.minimumOf, rule.min]);
      if (rule.max != null) parts.push([script.dataset.maximumOf, rule.max]);
      rulesText.replaceChildren(...parts.filter(([text]) => text).map(([text, quantity]) => {
        const span = document.createElement('span');
        span.className = 'divider';
        span.textContent = text.replace('[quantity]', quantity);
        return span;
      }));
    }
    const inCart = quantityForm && quantityForm.querySelector('.quantity__rules-cart');
    if (inCart) {
      inCart.classList.toggle('hidden', cart === 0);
      const count = inCart.querySelector('.quantity-cart');
      if (count) count.textContent = cart;
    }
    const productInfo = input.closest('product-info');
    if (productInfo && typeof productInfo.setQuantityBoundries === 'function') {
      productInfo.setQuantityBoundries();
      return;
    }
    const value = Number(input.value);
    if (!Number.isInteger(value) || value < rule.min || (rule.max != null && value > rule.max) || (value - rule.min) % rule.increment) {
      input.value = rule.min;
    }
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

// HFS (FBL-003): lets a picker connected after DOMContentLoaded (readyState still 'interactive') sync at once.
if (document.readyState !== 'complete' && !window.hfsDomContentLoaded) {
  document.addEventListener('DOMContentLoaded', () => { window.hfsDomContentLoaded = true; }, { once: true });
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  connectedCallback() {
    this.chooseRequestedOptions();
    super.connectedCallback();
  }

  // HFS (M1): a link that names a variant opens with all of its options chosen (hfsRequestedOptions), sold out or
  // not; the first sync then shows its price, stock and photo, and Add says Sold out when it is.
  chooseRequestedOptions() {
    if (this.hfsRequestChosen) return;
    this.hfsRequestChosen = true;
    const { options, stale } = hfsRequestedOptions(this);
    let missing = stale;
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const name = radios.length ? (radios[0].dataset.optionName || '').toLowerCase() : '';
      if (!Object.prototype.hasOwnProperty.call(options, name)) return;
      const radio = radios.find((input) => input.value === options[name]);
      if (radio) radio.checked = true;
      else missing = true;
    });
    if (missing) hfsLinkNote(this);
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      const checkedRadio = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
      if (checkedRadio) {
        var selectedField = checkedRadio.dataset.optionName || checkedRadio.name;
        if (window.hfsPdp) window.hfsPdp.setOptionError(selectedField, false, fieldset);
        return checkedRadio.value;
      }
      return null; // or any default value you want to use
    });
  }

}

customElements.define('variant-radios', VariantRadios);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }

          if (html.querySelector('.grid__item')) {
            this.classList.add('product-recommendations--loaded');
          }
          // HFS (JS-25): custom.js starts the related-products carousel on this event (it used to poll forever)
          this.dispatchEvent(new CustomEvent('hfs:recommendations-loaded', { bubbles: true }));
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

customElements.define('product-recommendations', ProductRecommendations);
